import { Badge, Button, EmptyState, Spinner, Table, type TableColumn } from '@bennows/boows-react';
import { CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime } from '../../lib/format';
import { ProblemBadges } from './ProblemBadges';
import { useProblems, type TenantProblems } from './useProblems';

const CARD_BREAKPOINT = 640;

/** Tenants with failed posts, broken connections or failed AI runs, most errors first (REQ-140-015). */
export function ProblemsPage() {
  const { t } = useTranslation();
  const problems = useProblems();
  const name = (row: TenantProblems) => (
    <span className="ui-console-name">
      <Link className="ui-console-name__link" to={`/tenants/${row.tenant_id}`}>
        {row.name}
      </Link>
      {row.suspended ? <Badge tone="danger">{t('tenant.suspendedBadge')}</Badge> : null}
    </span>
  );
  const columns: TableColumn<TenantProblems>[] = [
    { key: 'company', header: t('problems.columns.company'), cell: name },
    { key: 'problems', header: t('problems.columns.problems'), cell: (row) => <ProblemBadges problems={row.problems} /> },
  ];
  const metricsAt = problems.data?.metrics_at;
  return (
    <div className="ui-stack">
      <PageHeader title={t('problems.title')} description={metricsAt ? t('problems.asOf', { time: formatDateTime(metricsAt) }) : t('problems.description')} />
      {problems.isPending ? (
        <div className="ui-console-loading" role="status">
          <Spinner label={t('common.loading')} />
        </div>
      ) : problems.isError ? (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t('problems.loadError')}</p>
          <Button variant="secondary" onClick={() => void problems.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <Table
          ariaLabel={t('problems.listLabel')}
          rows={problems.data.items}
          columns={columns}
          getRowId={(row) => row.tenant_id}
          emptyState={<EmptyState icon={<CircleCheck aria-hidden="true" />} title={t('problems.emptyTitle')} hint={t('problems.emptyText')} />}
          labels={{ empty: t('problems.emptyTitle') }}
          cardBreakpoint={CARD_BREAKPOINT}
          viewToggle={false}
          mobileViewToggle={false}
          renderCard={(row) => (
            <div className="ui-console-card">
              {name(row)}
              <ProblemBadges problems={row.problems} />
            </div>
          )}
        />
      )}
    </div>
  );
}
